import type {
  GetTitleJournalResponse,
  ProjectParticipant,
} from "@/entities/api/journal/journal-title-types";

export type TitleOrgRole =
  | "buildingContractor"
  | "developer"
  | "technicalCustomer"
  | "projectDocumentationContractor"
  | "designerSupervision"
  | "otherDeveloper";

export interface TitleJournalCompanyOption {
  companyId: string;
  projectParticipantId: string;
  shortName: string;
  fullName: string;
  role: TitleOrgRole;
}

export interface UserTitleCompanyMatch {
  role: TitleOrgRole;
  companyId: string;
  projectParticipantId: string;
  isLos: boolean;
}

const TITLE_ORG_ROLE_PRIORITY: Record<TitleOrgRole, number> = {
  buildingContractor: 0,
  developer: 1,
  technicalCustomer: 2,
  projectDocumentationContractor: 3,
  designerSupervision: 4,
  otherDeveloper: 5,
};

function shouldReplaceTitleOrgRole(
  current: TitleOrgRole,
  candidate: TitleOrgRole,
): boolean {
  return (
    TITLE_ORG_ROLE_PRIORITY[candidate] < TITLE_ORG_ROLE_PRIORITY[current]
  );
}

function toCompanyOption(
  participant: ProjectParticipant,
  role: TitleOrgRole,
): TitleJournalCompanyOption | null {
  const companyId = participant.company?.companyId;
  const { projectParticipantId } = participant;

  if (!companyId || !projectParticipantId) return null;

  return {
    companyId,
    projectParticipantId,
    shortName: participant.company?.shortName ?? "",
    fullName: participant.company?.fullName ?? "",
    role,
  };
}

export function getTitleJournalCompanies(
  titleJournal: GetTitleJournalResponse | undefined,
): TitleJournalCompanyOption[] {
  const title = titleJournal?.mainJournal?.titleJournal;
  if (!title) return [];

  const entries: TitleJournalCompanyOption[] = [];

  const push = (
    participant: ProjectParticipant | null | undefined,
    role: TitleOrgRole,
  ) => {
    if (!participant) return;
    const option = toCompanyOption(participant, role);
    if (option) entries.push(option);
  };

  push(title.buildingContractor, "buildingContractor");
  push(title.developer, "developer");
  push(title.technicalCustomer, "technicalCustomer");
  push(title.projectDocumentationContractor, "projectDocumentationContractor");

  for (const participant of title.designerSupervisions ?? []) {
    push(participant, "designerSupervision");
  }

  for (const participant of title.otherDevelopers ?? []) {
    push(participant, "otherDeveloper");
  }

  const unique = new Map<string, TitleJournalCompanyOption>();
  for (const entry of entries) {
    const existing = unique.get(entry.projectParticipantId);
    if (!existing || shouldReplaceTitleOrgRole(existing.role, entry.role)) {
      unique.set(entry.projectParticipantId, entry);
    }
  }

  return Array.from(unique.values());
}

export function getCompanyDisplayName(company: {
  shortName?: string;
  fullName?: string;
}): string {
  return company.shortName || company.fullName || "—";
}

export function resolveUserTitleCompanyMatch(
  titleJournal: GetTitleJournalResponse | undefined,
  companyIds: string[],
): UserTitleCompanyMatch | null {
  if (!companyIds.length) return null;

  const title = titleJournal?.mainJournal?.titleJournal;
  const companyIdSet = new Set(companyIds);

  const losCompanyId = title?.buildingContractor?.company?.companyId;
  const losProjectParticipantId = title?.buildingContractor?.projectParticipantId;

  if (
    losCompanyId &&
    losProjectParticipantId &&
    companyIdSet.has(losCompanyId)
  ) {
    return {
      role: "buildingContractor",
      companyId: losCompanyId,
      projectParticipantId: losProjectParticipantId,
      isLos: true,
    };
  }

  const titleCompanies = getTitleJournalCompanies(titleJournal);

  const losMatch = titleCompanies.find(
    (company) =>
      company.role === "buildingContractor" &&
      companyIdSet.has(company.companyId),
  );

  if (losMatch) {
    return {
      role: losMatch.role,
      companyId: losMatch.companyId,
      projectParticipantId: losMatch.projectParticipantId,
      isLos: true,
    };
  }

  const otherMatch = titleCompanies.find((company) =>
    companyIdSet.has(company.companyId),
  );

  if (!otherMatch) return null;

  return {
    role: otherMatch.role,
    companyId: otherMatch.companyId,
    projectParticipantId: otherMatch.projectParticipantId,
    isLos: false,
  };
}
